import { useState, useEffect } from "react";
import { Plus, Pencil, Trash2, Eye, EyeOff, X, Save } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

const empty = {
  quote_fr: "",
  quote_en: "",
  name: "",
  location: "",
  country: "",
  image_url: "",
  published: true,
};

const TestimonialsManager = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | id
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/testimonials");
      setItems(data || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const startNew = () => {
    setForm(empty);
    setEditing("new");
  };

  const startEdit = (item) => {
    setForm({
      quote_fr: item.quote_fr || "",
      quote_en: item.quote_en || "",
      name: item.name || "",
      location: item.location || "",
      country: item.country || "",
      image_url: item.image_url || "",
      published: item.published !== false,
    });
    setEditing(item.id);
  };

  const cancel = () => {
    setEditing(null);
    setForm(empty);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editing === "new") {
        await api.post("/testimonials", form);
        toast.success("Témoignage ajouté");
      } else {
        await api.put(`/testimonials/${editing}`, form);
        toast.success("Témoignage mis à jour");
      }
      cancel();
      fetchAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const togglePublish = async (item) => {
    try {
      await api.put(`/testimonials/${item.id}`, { published: !item.published });
      fetchAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Supprimer le témoignage de ${item.name} ?`)) return;
    try {
      await api.delete(`/testimonials/${item.id}`);
      toast.success("Témoignage supprimé");
      fetchAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="testimonials-manager">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-[#64748B]">
          Gérez les témoignages affichés en page d'accueil. 3 maximum sont visibles.
        </p>
        <button
          onClick={startNew}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#002B49] text-white hover:bg-[#001F35] text-sm font-medium"
          data-testid="testimonial-new-btn"
        >
          <Plus size={14} /> Nouveau
        </button>
      </div>

      {/* Edit modal-like inline panel */}
      {editing && (
        <div className="bg-white rounded-2xl border-2 border-[#C5A059]/40 p-6 mb-6" data-testid="testimonial-form">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-serif text-xl text-[#002B49] font-semibold">
              {editing === "new" ? "Nouveau témoignage" : "Modifier le témoignage"}
            </h4>
            <button onClick={cancel} className="p-1 hover:bg-black/5 rounded-md" aria-label="close">
              <X size={18} className="text-[#64748B]" />
            </button>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Nom" required>
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="form-input-sm"
                data-testid="t-name-input"
              />
            </Field>
            <Field label="Localisation" required>
              <input
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                className="form-input-sm"
                placeholder="Ex: Bamako, Mali"
                data-testid="t-location-input"
              />
            </Field>
          </div>

          <Field label="Pays (pour filtre)" className="mt-4">
            <input
              value={form.country}
              onChange={(e) => setForm({ ...form, country: e.target.value })}
              className="form-input-sm"
              placeholder="Ex: Mali, Liban, RDC..."
              data-testid="t-country-input"
            />
          </Field>

          <Field label="Citation (Français)" required className="mt-4">
            <textarea
              rows="3"
              value={form.quote_fr}
              onChange={(e) => setForm({ ...form, quote_fr: e.target.value })}
              className="form-input-sm resize-none"
              data-testid="t-quote-fr-input"
            />
          </Field>

          <Field label="Citation (English)" required className="mt-4">
            <textarea
              rows="3"
              value={form.quote_en}
              onChange={(e) => setForm({ ...form, quote_en: e.target.value })}
              className="form-input-sm resize-none"
              data-testid="t-quote-en-input"
            />
          </Field>

          <Field label="URL image (optionnel)" className="mt-4">
            <input
              value={form.image_url}
              onChange={(e) => setForm({ ...form, image_url: e.target.value })}
              className="form-input-sm"
              placeholder="https://..."
              data-testid="t-image-input"
            />
          </Field>

          <label className="flex items-center gap-2 mt-4 text-sm text-[#334155] cursor-pointer">
            <input
              type="checkbox"
              checked={form.published}
              onChange={(e) => setForm({ ...form, published: e.target.checked })}
              className="rounded"
              data-testid="t-published-input"
            />
            Publié sur la page d'accueil
          </label>

          <div className="flex gap-3 mt-6">
            <button
              onClick={save}
              disabled={saving || !form.name || !form.location || form.quote_fr.length < 5 || form.quote_en.length < 5}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-[#C5A059] hover:bg-[#B38F48] text-white font-medium disabled:opacity-50"
              data-testid="t-save-btn"
            >
              <Save size={14} /> {saving ? "..." : "Enregistrer"}
            </button>
            <button
              onClick={cancel}
              className="px-5 py-2.5 rounded-md border border-[#E5E7EB] text-[#64748B] hover:bg-black/5"
              data-testid="t-cancel-btn"
            >
              Annuler
            </button>
          </div>
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="bg-white rounded-2xl p-8 text-center text-[#64748B]">Chargement...</div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center text-[#64748B]" data-testid="no-testimonials">
          Aucun témoignage. Cliquez sur « Nouveau » pour ajouter le premier.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((item, i) => (
            <div
              key={item.id}
              className="bg-white rounded-xl border border-black/5 p-5 flex items-start justify-between gap-4"
              data-testid={`testimonial-row-${i}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-[#0A192F]">{item.name}</span>
                  <span className="text-sm text-[#64748B]">— {item.location}</span>
                  <span
                    className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider ${
                      item.published
                        ? "bg-emerald-100 text-emerald-800"
                        : "bg-gray-200 text-gray-600"
                    }`}
                  >
                    {item.published ? "Publié" : "Caché"}
                  </span>
                </div>
                <p className="text-sm text-[#334155] mt-2 italic line-clamp-2">« {item.quote_fr} »</p>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <button
                  onClick={() => togglePublish(item)}
                  className="p-2 hover:bg-black/5 rounded-md text-[#64748B] hover:text-[#002B49]"
                  title={item.published ? "Masquer" : "Publier"}
                  data-testid={`t-toggle-${i}`}
                >
                  {item.published ? <Eye size={16} /> : <EyeOff size={16} />}
                </button>
                <button
                  onClick={() => startEdit(item)}
                  className="p-2 hover:bg-black/5 rounded-md text-[#64748B] hover:text-[#002B49]"
                  title="Modifier"
                  data-testid={`t-edit-${i}`}
                >
                  <Pencil size={16} />
                </button>
                <button
                  onClick={() => remove(item)}
                  className="p-2 hover:bg-red-50 rounded-md text-[#64748B] hover:text-red-600"
                  title="Supprimer"
                  data-testid={`t-delete-${i}`}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <style>{`
        .form-input-sm {
          width: 100%;
          margin-top: 0.4rem;
          padding: 0.6rem 0.85rem;
          border-radius: 0.5rem;
          border: 1px solid #E5E7EB;
          outline: none;
          transition: border-color 0.2s, box-shadow 0.2s;
          font-size: 0.9rem;
          color: #0A192F;
          background: #FDFBF7;
        }
        .form-input-sm:focus {
          border-color: #002B49;
          box-shadow: 0 0 0 3px rgba(0, 43, 73, 0.1);
          background: #FFF;
        }
      `}</style>
    </div>
  );
};

const Field = ({ label, required, children, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="text-xs font-medium text-[#64748B] uppercase tracking-wider">
      {label} {required && <span className="text-[#C5A059]">*</span>}
    </span>
    {children}
  </label>
);

export default TestimonialsManager;
